import { useCallback, useEffect, useState } from "react";
import {
  completeManufacturingBuiltUnitBuild,
  createManufacturingUnit,
  listManufacturingPlants,
  listManufacturingUnits,
  startManufacturingBuiltUnitBuild,
  type MfgPlant,
  type MfgUnit,
  type MfgUnitSalePath,
  type MfgUnitVisibility,
} from "@/lib/manufacturingClient";

const SALE_PATHS: MfgUnitSalePath[] = ["inventory_no_sale_yet", "cash", "finance", "rto"];
const VISIBILITIES: MfgUnitVisibility[] = ["private", "org_internal", "cross_dealer_marketplace"];

export type UnitsListFilter = {
  plant_id: string;
  sale_path: string;
};

export function useManufacturingUnitsTab(unitsNavTick: number, unitsNewSignal: number) {
  const [units, setUnits] = useState<MfgUnit[]>([]);
  const [plants, setPlants] = useState<MfgPlant[]>([]);
  const [listBusy, setListBusy] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  // Filter state — backed by the corresponding API query params.
  const [filter, setFilter] = useState<UnitsListFilter>({ plant_id: "", sale_path: "" });

  // Create form state.
  const [creating, setCreating] = useState(false);
  const [newPlantId, setNewPlantId] = useState("");
  const [newRecipeId, setNewRecipeId] = useState("");
  const [newSalePath, setNewSalePath] = useState<MfgUnitSalePath>("inventory_no_sale_yet");
  const [newVisibility, setNewVisibility] = useState<MfgUnitVisibility>("private");
  const [newInventoryHolderOrgId, setNewInventoryHolderOrgId] = useState("");
  const [newEndCustomerContactId, setNewEndCustomerContactId] = useState("");
  const [newOpsDealId, setNewOpsDealId] = useState("");
  const [newNotes, setNewNotes] = useState("");

  const reloadPlants = useCallback(async () => {
    const res = await listManufacturingPlants();
    if (!res.ok) {
      // Plants failure is non-fatal for the units list; surface as warning.
      setPlants([]);
      return;
    }
    setPlants(Array.isArray(res.data.plants) ? res.data.plants : []);
  }, []);

  const reloadUnits = useCallback(async () => {
    setListBusy(true);
    setLocalError(null);
    const res = await listManufacturingUnits({
      plant_id: filter.plant_id || undefined,
      sale_path: filter.sale_path || undefined,
    });
    setListBusy(false);
    if (!res.ok) {
      setUnits([]);
      setLocalError(res.error);
      return;
    }
    setUnits(Array.isArray(res.data.units) ? res.data.units : []);
  }, [filter.plant_id, filter.sale_path]);

  useEffect(() => {
    void reloadPlants();
  }, [reloadPlants, unitsNavTick]);

  useEffect(() => {
    void reloadUnits();
  }, [reloadUnits, unitsNavTick]);

  // Default the create form's plant_id to the org's default plant once the
  // plants list arrives, so the user doesn't have to pick the obvious choice.
  useEffect(() => {
    if (newPlantId) return;
    const def = plants.find((p) => p.is_default);
    if (def) setNewPlantId(def.id);
  }, [plants, newPlantId]);

  useEffect(() => {
    if (unitsNewSignal === 0) return;
    setCreating(true);
    setLocalError(null);
    // Reset transient form state but keep newPlantId (the default plant is sticky).
    setNewRecipeId("");
    setNewSalePath("inventory_no_sale_yet");
    setNewVisibility("private");
    setNewInventoryHolderOrgId("");
    setNewEndCustomerContactId("");
    setNewOpsDealId("");
    setNewNotes("");
  }, [unitsNewSignal]);

  const cancelCreate = useCallback(() => {
    setCreating(false);
    setLocalError(null);
  }, []);

  const startBuildForUnit = useCallback(
    async (unitId: string) => {
      setActionBusy(true);
      setLocalError(null);
      const res = await startManufacturingBuiltUnitBuild({ mfg_unit_id: unitId });
      setActionBusy(false);
      if (!res.ok) {
        setLocalError(res.error);
        return;
      }
      await reloadUnits();
    },
    [reloadUnits],
  );

  const completeBuildForUnit = useCallback(
    async (unitId: string) => {
      setActionBusy(true);
      setLocalError(null);
      const res = await completeManufacturingBuiltUnitBuild({ mfg_unit_id: unitId });
      setActionBusy(false);
      if (!res.ok) {
        setLocalError(res.error);
        return;
      }
      await reloadUnits();
    },
    [reloadUnits],
  );

  const submitCreate = useCallback(async () => {
    const plant_id = newPlantId.trim();
    if (!plant_id) {
      setLocalError("Plant is required.");
      return;
    }
    setActionBusy(true);
    setLocalError(null);
    const res = await createManufacturingUnit({
      plant_id,
      recipe_id: newRecipeId.trim() || undefined,
      sale_path: newSalePath,
      visibility: newVisibility,
      inventory_holder_org_id: newInventoryHolderOrgId.trim() || undefined,
      end_customer_contact_id: newEndCustomerContactId.trim() || undefined,
      ops_deal_id: newOpsDealId.trim() || undefined,
      notes: newNotes.trim() || undefined,
    });
    setActionBusy(false);
    if (!res.ok) {
      setLocalError(res.error);
      return;
    }
    setCreating(false);
    await reloadUnits();
  }, [
    newPlantId,
    newRecipeId,
    newSalePath,
    newVisibility,
    newInventoryHolderOrgId,
    newEndCustomerContactId,
    newOpsDealId,
    newNotes,
    reloadUnits,
  ]);

  return {
    units,
    plants,
    listBusy,
    actionBusy,
    localError,
    filter,
    setFilter,
    creating,
    newPlantId,
    setNewPlantId,
    newRecipeId,
    setNewRecipeId,
    newSalePath,
    setNewSalePath,
    newVisibility,
    setNewVisibility,
    newInventoryHolderOrgId,
    setNewInventoryHolderOrgId,
    newEndCustomerContactId,
    setNewEndCustomerContactId,
    newOpsDealId,
    setNewOpsDealId,
    newNotes,
    setNewNotes,
    cancelCreate,
    submitCreate,
    startBuildForUnit,
    completeBuildForUnit,
    reload: reloadUnits,
    salePathOptions: SALE_PATHS,
    visibilityOptions: VISIBILITIES,
  };
}
